import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, Building2, Sparkles } from "lucide-react";
import MarketingLayout from "../../components/marketing/MarketingLayout";

export default function AboutPage() {
  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 -z-10">
          <div className="absolute -top-40 right-0 w-[700px] h-[700px] rounded-full bg-blue-100 dark:bg-blue-950/40 blur-3xl opacity-40" />
        </div>
        <div className="max-w-6xl mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-16">
          <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-4">About</div>
          <h1 className="font-outfit text-5xl md:text-6xl font-black text-slate-900 dark:text-white leading-[1.05] max-w-4xl">
            An intelligence company, built for the modern organization.
          </h1>
          <p className="mt-6 max-w-2xl font-ibm-plex text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            SYNQ is a brand of <span className="text-slate-900 dark:text-white font-semibold">RamRath Global (GA)</span> —
            an intelligence platform company building the tools that help organizations understand themselves.
          </p>
        </div>
      </section>

      {/* Company / Brand / Product structure */}
      <section className="border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-20">
          <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-3">Structure</div>
          <h2 className="font-outfit text-3xl md:text-4xl font-black text-slate-900 dark:text-white leading-tight mb-12">Who builds SYNQ.</h2>

          <div className="grid md:grid-cols-3 gap-5">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8">
              <div className="w-11 h-11 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center mb-5">
                <Building2 className="w-5 h-5" />
              </div>
              <div className="font-ibm-plex text-[10px] uppercase tracking-widest text-slate-500 mb-1">Company</div>
              <h3 className="font-outfit text-2xl font-black text-slate-900 dark:text-white">RamRath Global (GA)</h3>
              <p className="font-ibm-plex text-slate-600 dark:text-slate-400 mt-3 leading-relaxed">
                The intelligence platform company. Builder of SYNQ and every product in the SYNQ ecosystem.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8">
              <div className="w-11 h-11 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center mb-5">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="font-ibm-plex text-[10px] uppercase tracking-widest text-slate-500 mb-1">Brand</div>
              <h3 className="font-outfit text-2xl font-black text-slate-900 dark:text-white">SYNQ</h3>
              <p className="font-ibm-plex text-slate-600 dark:text-slate-400 mt-3 leading-relaxed">
                The Intelligence Platform for Modern Organizations. Every product belongs to the SYNQ ecosystem.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8">
              <div className="w-11 h-11 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center mb-5">
                <Compass className="w-5 h-5" />
              </div>
              <div className="font-ibm-plex text-[10px] uppercase tracking-widest text-slate-500 mb-1">Products</div>
              <h3 className="font-outfit text-2xl font-black text-slate-900 dark:text-white">Intelligence Platforms</h3>
              <p className="font-ibm-plex text-slate-600 dark:text-slate-400 mt-3 leading-relaxed">
                SYNQ Projects, SYNQ Evolve — and more intelligence platforms on the way.
              </p>
            </div>
          </div>

          {/* Flow arrows on md+ */}
          <div className="hidden md:flex items-center justify-center gap-8 mt-10">
            <span className="font-outfit text-sm text-slate-500">RamRath Global (GA)</span>
            <span className="text-slate-400">→</span>
            <span className="font-outfit text-sm text-slate-500">created SYNQ</span>
            <span className="text-slate-400">→</span>
            <span className="font-outfit text-sm text-slate-500">multiple Intelligence Platforms</span>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-24 text-center">
          <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-3">Mission</div>
          <h2 className="font-outfit text-4xl md:text-5xl font-black text-slate-900 dark:text-white leading-tight">
            To build the world&rsquo;s most trusted<br />
            <span className="text-slate-500 dark:text-slate-500">Organizational Intelligence Platform.</span>
          </h2>
          <p className="mt-8 font-ibm-plex text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Every product we ship is a step towards a world where organizations understand themselves better than yesterday.
          </p>
          <div className="mt-10">
            <Link
              to="/contact?intent=demo"
              className="inline-flex items-center gap-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-3.5 rounded-full font-outfit font-semibold text-[15px] hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors"
            >
              Talk to us <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
